/**
 * Lightweight Sentry transport for the Cloudflare Worker deployment target.
 *
 * The full `@sentry/nextjs` integration pushed the worker bundle over the
 * Cloudflare free-plan limit, so this module sends minimal error envelopes
 * directly to Sentry using the public DSN.
 */

export interface SentryConfig {
  dsn: string;
  environment: string;
  release?: string;
  tracesSampleRate: number;
  replaysSampleRate: number;
  replaysOnErrorSampleRate: number;
}

interface SentryUser {
  id?: string;
  email?: string;
  username?: string;
}

interface CaptureContext {
  request?: {
    path?: string;
    method?: string;
    headers?: Record<string, string | string[] | undefined>;
  };
  tags?: Record<string, string>;
  user?: SentryUser | null;
  [key: string]: unknown;
}

interface ParsedDsn {
  apiBase: string;
  publicKey: string;
  projectId: string;
}

interface SentryFrame {
  filename?: string;
  function?: string;
  lineno?: number;
  colno?: number;
  in_app?: boolean;
}

interface SentryState {
  user: SentryUser | null;
}

declare global {
  var __MAPPITO_SENTRY_STATE__: SentryState | undefined;
}

const SDK_NAME = "mappito.sentry-lite";
const SDK_VERSION = "1.0.0";

function getState(): SentryState {
  const root = globalThis as typeof globalThis & {
    __MAPPITO_SENTRY_STATE__?: SentryState;
  };
  root.__MAPPITO_SENTRY_STATE__ ??= { user: null };
  return root.__MAPPITO_SENTRY_STATE__;
}

export function getSentryConfig(): SentryConfig | null {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  if (!dsn) return null;

  return {
    dsn,
    environment:
      process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
      process.env.NODE_ENV ??
      "development",
    release:
      process.env.NEXT_PUBLIC_SENTRY_RELEASE ??
      process.env.VERCEL_GIT_COMMIT_SHA,
    tracesSampleRate: parseFloat(
      process.env.SENTRY_TRACES_SAMPLE_RATE ?? "0.1",
    ),
    replaysSampleRate: parseFloat(
      process.env.SENTRY_REPLAYS_SAMPLE_RATE ?? "0.1",
    ),
    replaysOnErrorSampleRate: parseFloat(
      process.env.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? "1.0",
    ),
  };
}

export function isSentryEnabled(): boolean {
  return !!process.env.NEXT_PUBLIC_SENTRY_DSN;
}

function parseDsn(dsn: string): ParsedDsn | null {
  try {
    const url = new URL(dsn);
    const pathSegments = url.pathname.split("/").filter(Boolean);
    const projectId = pathSegments.pop();

    if (!projectId || !url.username) {
      return null;
    }

    const pathPrefix = pathSegments.length ? `/${pathSegments.join("/")}` : "";

    return {
      apiBase: `${url.protocol}//${url.host}${pathPrefix}/api`,
      publicKey: url.username,
      projectId,
    };
  } catch {
    return null;
  }
}

function getEnvelopeEndpoint(dsn: string): string | null {
  const parsed = parseDsn(dsn);
  if (!parsed) return null;

  const query = new URLSearchParams({
    sentry_version: "7",
    sentry_key: parsed.publicKey,
    sentry_client: `${SDK_NAME}/${SDK_VERSION}`,
  });

  return `${parsed.apiBase}/${parsed.projectId}/envelope/?${query.toString()}`;
}

function normalizeValue(value: unknown, depth = 3): unknown {
  if (depth <= 0) return "[Truncated]";
  if (value == null) return value;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => normalizeValue(item, depth - 1));
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const entries = Object.entries(record).slice(0, 20);
    return Object.fromEntries(
      entries.map(([key, entry]) => [key, normalizeValue(entry, depth - 1)]),
    );
  }
  return String(value);
}

function parseStackFrames(stack?: string): SentryFrame[] | undefined {
  if (!stack) return undefined;

  const frames: SentryFrame[] = [];
  const lines = stack.split("\n").slice(1);

  for (const line of lines) {
    const trimmed = line.trim();
    const match =
      /^at\s+(?:(.*?)\s+\()?(.+?):(\d+):(\d+)\)?$/.exec(trimmed) ??
      /^(.*?)(?:@)(.+?):(\d+):(\d+)$/.exec(trimmed);

    if (!match) continue;

    const [, fn, filename, lineNo, colNo] = match;
    frames.push({
      filename,
      function: fn || "<anonymous>",
      lineno: Number(lineNo),
      colno: Number(colNo),
      in_app: true,
    });
  }

  return frames.length ? frames.reverse() : undefined;
}

function toError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === "string") return new Error(error);
  return new Error(JSON.stringify(normalizeValue(error)));
}

function buildRequestContext(request?: CaptureContext["request"]) {
  if (!request) return undefined;

  const path = request.path ?? "/";
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const url =
    siteUrl && path.startsWith("/")
      ? `${siteUrl.replace(/\/$/, "")}${path}`
      : path;

  return {
    url,
    method: request.method,
    headers: normalizeValue(request.headers) as Record<string, unknown>,
  };
}

function buildBaseEvent(
  level: "info" | "warning" | "error",
  context?: CaptureContext,
) {
  const config = getSentryConfig();
  const state = getState();
  const eventId =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now()}${Math.random().toString(16).slice(2, 10)}`;

  const extra = normalizeValue(
    Object.fromEntries(
      Object.entries(context ?? {}).filter(
        ([key]) => key !== "tags" && key !== "request" && key !== "user",
      ),
    ),
  ) as Record<string, unknown>;

  return {
    event_id: eventId,
    timestamp: new Date().toISOString(),
    platform: "javascript",
    level,
    environment: config?.environment,
    release: config?.release,
    sdk: {
      name: SDK_NAME,
      version: SDK_VERSION,
    },
    tags: {
      runtime: typeof window === "undefined" ? "server" : "browser",
      ...(context?.tags ?? {}),
    },
    user: context?.user ?? state.user ?? undefined,
    request: buildRequestContext(context?.request),
    extra,
  };
}

function buildErrorEvent(error: unknown, context?: CaptureContext) {
  const normalized = toError(error);

  return {
    ...buildBaseEvent("error", context),
    exception: {
      values: [
        {
          type: normalized.name || "Error",
          value: normalized.message || "Unknown error",
          stacktrace: (() => {
            const frames = parseStackFrames(normalized.stack);
            return frames ? { frames } : undefined;
          })(),
        },
      ],
    },
  };
}

function buildMessageEvent(
  message: string,
  level: "info" | "warning" | "error",
  context?: CaptureContext,
) {
  return {
    ...buildBaseEvent(level, context),
    message,
  };
}

async function sendEnvelope(payload: Record<string, unknown>): Promise<void> {
  const config = getSentryConfig();
  if (!config || typeof fetch !== "function") return;

  const endpoint = getEnvelopeEndpoint(config.dsn);
  if (!endpoint) return;

  const envelope =
    `${JSON.stringify({ sent_at: new Date().toISOString() })}\n` +
    `${JSON.stringify({ type: "event" })}\n` +
    JSON.stringify(payload);

  try {
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "content-type": "text/plain;charset=UTF-8",
      },
      body: envelope,
      keepalive: typeof window !== "undefined",
    });
  } catch {
    // Best-effort reporting only.
  }
}

export async function captureErrorAsync(
  error: unknown,
  context?: CaptureContext,
): Promise<void> {
  const sentry = (globalThis as { __SENTRY__?: { captureException?: (err: unknown, meta?: Record<string, unknown>) => void } })
    .__SENTRY__;

  if (sentry?.captureException) {
    sentry.captureException(error, { extra: context });
    return;
  }

  if (!isSentryEnabled()) {
    console.error("[Sentry fallback]", error, context);
    return;
  }

  await sendEnvelope(buildErrorEvent(error, context));
}

export function captureError(
  error: unknown,
  context?: CaptureContext,
): void {
  void captureErrorAsync(error, context);
}

export async function captureMessageAsync(
  message: string,
  level: "info" | "warning" | "error" = "info",
  context?: CaptureContext,
): Promise<void> {
  const sentry = (globalThis as { __SENTRY__?: { captureMessage?: (msg: string, meta?: Record<string, unknown>) => void } })
    .__SENTRY__;

  if (sentry?.captureMessage) {
    sentry.captureMessage(message, { level, extra: context });
    return;
  }

  if (!isSentryEnabled()) {
    const logFn =
      level === "error"
        ? console.error
        : level === "warning"
          ? console.warn
          : console.warn;
    logFn(`[Sentry fallback] ${message}`, context);
    return;
  }

  await sendEnvelope(buildMessageEvent(message, level, context));
}

export function captureMessage(
  message: string,
  level: "info" | "warning" | "error" = "info",
  context?: CaptureContext,
): void {
  void captureMessageAsync(message, level, context);
}

export function setSentryUser(user: SentryUser | null): void {
  getState().user = user;

  const sentry = (globalThis as { __SENTRY__?: { setUser?: (nextUser: SentryUser | null) => void } })
    .__SENTRY__;
  sentry?.setUser?.(user);
}

export function startSpan(): { finish: () => void } {
  return { finish: () => {} };
}
